import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ServiceCapacityCalculator = () => {
  const [guestCount, setGuestCount] = useState('');
  const [eventType, setEventType] = useState('');
  const [mealType, setMealType] = useState('');
  const [calculation, setCalculation] = useState(null);

  const eventTypeOptions = [
    { value: 'corporate-meeting', label: 'Corporate Meeting' },
    { value: 'wedding', label: 'Wedding Reception' },
    { value: 'birthday', label: 'Birthday Celebration' },
    { value: 'conference', label: 'Conference/Seminar' },
    { value: 'holiday-party', label: 'Holiday Party' },
    { value: 'other', label: 'Other Event' }
  ];

  const mealTypeOptions = [
    { value: 'breakfast', label: 'Breakfast Service' },
    { value: 'lunch', label: 'Lunch Service' },
    { value: 'dinner', label: 'Dinner Service' },
    { value: 'appetizers', label: 'Appetizers Only' },
    { value: 'dessert', label: 'Dessert Bar' },
    { value: 'full-service', label: 'Full Day Service' }
  ];

  const calculateCapacity = () => {
    if (!guestCount || !eventType || !mealType) return;

    const guests = parseInt(guestCount);
    const basePrice = {
      'breakfast': 18,
      'lunch': 25,
      'dinner': 35,
      'appetizers': 15,
      'dessert': 12,
      'full-service': 65
    };

    const pricePerPerson = basePrice?.[mealType] || 25;
    const estimatedCost = guests * pricePerPerson;
    const setupTime = guests > 100 ? '2-3 hours' : '1-2 hours';
    const staffRequired = Math.ceil(guests / 25);

    setCalculation({
      guests,
      pricePerPerson,
      estimatedCost,
      setupTime,
      staffRequired,
      itemsNeeded: Math.ceil(guests * 1.2)
    });
  };

  return (
    <section className="py-16 bg-card">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 mb-4">
              <Icon name="Calculator" size={18} color="var(--color-primary)" />
              <span className="text-sm font-medium text-primary">Planning Tool</span>
            </div>
            <h2 className="font-serif text-4xl font-bold text-foreground mb-4">
              Event Capacity Calculator
            </h2>
            <p className="text-lg text-muted-foreground">
              Get instant estimates for your event requirements
            </p>
          </div>

          <div className="bg-background rounded-2xl shadow-warm p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Input
                label="Number of Guests"
                type="number"
                placeholder="Enter guest count"
                value={guestCount}
                onChange={(e) => setGuestCount(e?.target?.value)}
                required
              />

              <Select
                label="Event Type"
                placeholder="Select event type"
                options={eventTypeOptions}
                value={eventType}
                onChange={setEventType}
                required
              />

              <Select
                label="Meal Service"
                placeholder="Select meal type"
                options={mealTypeOptions}
                value={mealType}
                onChange={setMealType}
                required
              />
            </div>

            <Button
              variant="default"
              size="lg"
              fullWidth
              iconName="Calculator"
              iconPosition="left"
              onClick={calculateCapacity}
              disabled={!guestCount || !eventType || !mealType}
            >
              Calculate Requirements
            </Button>

            {calculation && (
              <div className="mt-8 pt-8 border-t border-border">
                <h3 className="font-serif text-2xl font-semibold text-foreground mb-6">
                  Your Event Estimate
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-card rounded-xl p-6 border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="DollarSign" size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Estimated Cost</div>
                        <div className="text-2xl font-bold text-foreground">
                          ${calculation?.estimatedCost?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      ${calculation?.pricePerPerson} per person
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-6 border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Icon name="Clock" size={20} color="var(--color-accent)" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Setup Time</div>
                        <div className="text-2xl font-bold text-foreground">
                          {calculation?.setupTime}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Before event start
                    </div>
                  </div>

                  <div className="bg-card rounded-xl p-6 border border-border">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon name="Users" size={20} color="var(--color-success)" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Staff Required</div>
                        <div className="text-2xl font-bold text-foreground">
                          {calculation?.staffRequired}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Professional servers
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-accent/10 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} color="var(--color-accent)" className="mt-0.5" />
                    <div className="text-sm text-foreground">
                      <strong>Note:</strong> This is an estimate based on standard service. Final pricing may vary based on menu selections, service style, and additional requirements. Contact us for a detailed quote.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceCapacityCalculator;